import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import ProGate from "@/components/ProGate";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Star, Radar, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { CHAIN_META } from "@/lib/format";

function WatchlistInner() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    const { data } = await api.get("/watchlist");
    setItems(data); setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const remove = async (id) => { await api.delete(`/watchlist/${id}`); toast.success("Removed"); load(); };
  const scan = (it) => nav("/scanner", { state: { scan: { chain: it.chain, address: it.address, symbol: it.symbol } } });

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-8 py-8">
      <h1 className="font-display text-3xl font-bold flex items-center gap-2 mb-1"><Star className="h-7 w-7 text-emerald-400" /> Watchlist</h1>
      <p className="text-slate-400 text-sm mb-6">Tokens you're stalking. One tap to re-scan.</p>

      {!loading && items.length === 0 && (
        <Card className="bg-[#111723] border-[#222F43] p-12 text-center" data-testid="empty-watchlist">
          <Star className="h-8 w-8 text-slate-600 mx-auto mb-3" />
          <p className="text-slate-400">Nothing here yet. Scan a token and hit <span className="text-emerald-400">Watchlist</span> to save it.</p>
          <Button onClick={() => nav("/scanner")} className="mt-4 bg-emerald-500 hover:bg-emerald-400 text-black font-bold"><Radar className="h-4 w-4 mr-1.5" /> Go scan</Button>
        </Card>
      )}

      <div className="grid sm:grid-cols-2 gap-3" data-testid="watchlist-grid">
        {items.map((it) => {
          const cm = CHAIN_META[it.chain] || { label: it.chain, color: "#94A3B8" };
          return (
            <Card key={it.id} className="bg-[#111723] border-[#222F43] p-4 flex items-center gap-3 hover:border-emerald-500/30 transition-colors" data-testid={`watch-${it.symbol}`}>
              {it.imageUrl ? <img src={it.imageUrl} alt="" className="h-10 w-10 rounded-full" /> :
                <div className="h-10 w-10 rounded-full bg-[#172030] flex items-center justify-center font-bold text-emerald-400 text-sm">{it.symbol?.slice(0, 2)}</div>}
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2"><span className="font-bold">${it.symbol}</span>
                  <Badge variant="outline" style={{ borderColor: cm.color + "66", color: cm.color }} className="bg-transparent text-[9px] uppercase">{cm.label}</Badge>
                </div>
                <div className="text-xs text-slate-500 truncate">{it.name}</div>
              </div>
              <Button size="sm" onClick={() => scan(it)} className="bg-emerald-500 hover:bg-emerald-400 text-black font-bold" data-testid={`scan-${it.symbol}`}><Radar className="h-4 w-4" /></Button>
              <button onClick={() => remove(it.id)} className="p-2 text-slate-400 hover:text-red-400" data-testid={`remove-${it.symbol}`}><Trash2 className="h-4 w-4" /></button>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

export default function Watchlist() {
  return <ProGate feature="Watchlist"><WatchlistInner /></ProGate>;
}
